import { SpecificUserKind, resolveLoggedInSpecificUser, compareByName } from "./EmailSenderHelper";

// ROI-372. The alert step "Specific user" list: one multi-select over the two saved arrays. Saved step
// JSON is unchanged:
//   userIds    -> Roi-AI users (Users.Id)
//   atsUserIds -> database users (ATS OwnerId)
// The two id ranges can hold the same number, so every option is keyed "<kind>-<id>".

const normaliseEmail = (email) => (email ?? "").trim().toLowerCase();
const asArray = (value) => (Array.isArray(value) ? value : []);

const toOption = (user, kind) => ({
  key: `${kind}-${user.id}`,
  kind,
  id: user.id,
  fullName: user.fullName ?? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
  email: user.email ?? ""
});

// Every database user, plus each Roi-AI user with no database user of the same email (ROI-236 rule).
// One entry per person, also for saved steps:
//   - a saved Roi-AI recipient that is now linked stays listed, and its database twin is hidden, so the
//     step keeps showing who it sends to
//   - a person saved in both arrays shows once, as the database user; the Roi-AI twin is hidden
// Two database users that share an email stay separate: they are separate ATS records.
export const buildAlertRecipientOptions = (atsUsers, roiUsers, formAlert = null) => {
  const savedUserIds = asArray(formAlert?.userIds);
  const savedAtsUserIds = asArray(formAlert?.atsUserIds);

  const databaseUsers = asArray(atsUsers);
  const databaseByEmail = new Map();
  databaseUsers.forEach(u => {
    const email = normaliseEmail(u.email);
    if (!email) return;
    if (!databaseByEmail.has(email)) databaseByEmail.set(email, []);
    databaseByEmail.get(email).push(u);
  });

  const hiddenDatabaseIds = new Set();
  const roiOptions = [];
  asArray(roiUsers).forEach(u => {
    const twins = databaseByEmail.get(normaliseEmail(u.email)) ?? [];
    if (twins.length === 0) {
      roiOptions.push(toOption(u, SpecificUserKind.RoiAi));
      return;
    }
    const isSaved = savedUserIds.includes(u.id);
    const isTwinSaved = twins.some(t => savedAtsUserIds.includes(t.id));
    if (isSaved && !isTwinSaved) {
      roiOptions.push(toOption(u, SpecificUserKind.RoiAi));
      twins.forEach(t => hiddenDatabaseIds.add(t.id));
    }
  });

  const databaseOptions = databaseUsers
    .filter(u => !hiddenDatabaseIds.has(u.id))
    .map(u => toOption(u, SpecificUserKind.Database));

  return [...databaseOptions, ...roiOptions]
    .sort((a, b) => compareByName(a.fullName, b.fullName));
};

const isSelected = (option, formAlert) => option.kind === SpecificUserKind.Database
  ? asArray(formAlert?.atsUserIds).includes(option.id)
  : asArray(formAlert?.userIds).includes(option.id);

export const findSelectedAlertRecipients = (formAlert, options) =>
  asArray(options).filter(o => isSelected(o, formAlert));

// The saved arrays after the user changes the selection. Visible picks are written by kind. Saved ids
// of people missing from the loaded lists (left the ATS, deactivated) are kept, so an unrelated edit
// never drops them. A hidden Roi-AI twin is in the loaded list, so it is dropped on edit and the
// person is saved once, as shown.
export const alertRecipientState = (selectedOptions, formAlert, atsUsers, roiUsers) => {
  const selected = asArray(selectedOptions);
  const loadedAtsIds = new Set(asArray(atsUsers).map(u => u.id));
  const loadedRoiIds = new Set(asArray(roiUsers).map(u => u.id));

  const keptUserIds = asArray(formAlert?.userIds).filter(id => !loadedRoiIds.has(id));
  const keptAtsUserIds = asArray(formAlert?.atsUserIds).filter(id => !loadedAtsIds.has(id));

  return {
    userIds: [...keptUserIds, ...selected.filter(o => o.kind === SpecificUserKind.RoiAi).map(o => o.id)],
    atsUserIds: [...keptAtsUserIds, ...selected.filter(o => o.kind === SpecificUserKind.Database).map(o => o.id)]
  };
};

// The logged-in user as the recipient of a new alert step: their database user when linked (ROI-236,
// or the same email rule before ROI-236 is deployed), otherwise their own Roi-AI user. null for
// someone in neither list, such as a super admin. Saved as a fixed id, never resolved at run time.
export const defaultAlertRecipientState = (user, options) => {
  const me = resolveLoggedInSpecificUser(user, options);
  if (!me) return null;
  return me.kind === SpecificUserKind.Database
    ? { userIds: [], atsUserIds: [me.id] }
    : { userIds: [me.id], atsUserIds: [] };
};

// Step card count. The card is drawn before the user lists load, so it counts saved ids.
export const countAlertRecipientUsers = (formAlert) =>
  asArray(formAlert?.userIds).length + asArray(formAlert?.atsUserIds).length;
